import { profileQueryOptions, profileSelectors } from "@stryker/core/queries";
import type { CompositeJob, MechanicalJob } from "@stryker/core/types/jobs";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import type { Dispatch, MouseEvent, SetStateAction } from "react";
import { useNavigate } from "react-router-dom";
import Status from "@/components/job/Status";
import { Button } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";
import { useConfigOptions } from "@/hooks/useConfigOptions";

type JobCardProps = {
  job: CompositeJob | MechanicalJob;
  jobType?: "composite" | "mechanical" | "all";
  setJobIdToDuplicate: (value: number | null) => void;
  setJobIdToRemove: Dispatch<SetStateAction<null | number>>;
};

export default function JobCard({ job, jobType, setJobIdToRemove, setJobIdToDuplicate }: JobCardProps) {
  const navigate = useNavigate();
  const { data: canDeleteJob } = useQuery({
    ...profileQueryOptions(),
    select: (profile) => profileSelectors.isAdmin(profile) || profileSelectors.isEngineer(profile),
  });

  const goToDetails = (e: MouseEvent<HTMLDivElement>) => {
    const target = e.target;
    if (!(target instanceof HTMLElement)) return;
    const dataAttribute = target.getAttribute("data");
    const hasDataSelectInParent = target.closest("[data-select]");
    const insideOfCard = target.closest('[data="update-job-info-card"]');
    const hasOpenSelect = !!document.querySelector("[data-radix-popper-content-wrapper]");
    if (
      dataAttribute !== "duplicate-btn" &&
      dataAttribute !== "delete-btn" &&
      dataAttribute !== "job-updates" &&
      dataAttribute !== "expand-btn" &&
      !hasDataSelectInParent &&
      !insideOfCard &&
      !hasOpenSelect
    ) {
      navigate(`/jobs/${job.id}`);
    }
  };

  const { repairSystemOptions } = useConfigOptions();

  const repairSystemTitle =
    "repair_system" in job
      ? (repairSystemOptions.find((r) => r.value === job.repair_system)?.title ?? job.repair_system)
      : "N/A";

  const repairTypeLabels: Record<string, string> = {
    straight_line: "Straight Line",
    tee: "Tee",
    long_radius: "Long Radius",
    flange_clamp: "Flange Clamp",
  };

  const repairType =
    "repair_type" in job ? (job.repair_type ? (repairTypeLabels[job.repair_type] ?? "N/A") : "N/A") : "N/A";

  const resolvedJobType = jobType ?? "all";
  const showRepairSystem = resolvedJobType === "all" || resolvedJobType === "composite";
  const showRepairType = resolvedJobType === "all" || resolvedJobType === "mechanical";

  return (
    <div onClick={goToDetails} className="rounded-xl border border-solid border-gray-200 px-4 py-5">
      <div className="mb-4 flex flex-wrap items-center gap-1">
        <span className="flex-2 max-w-[57%] text-ellipsis overflow-clip">{job.job_number}</span>
        <Typography className="flex-1">Date Run</Typography>
        <Typography className="flex-1">
          {job.date_run ? format(new Date(job.date_run), "MM.dd.yyyy") : "N/A"}
        </Typography>
        <Status status={job.job_status} className="ml-auto" />
      </div>
      <div className="mt-2 flex justify-between gap-4">
        <div className="flex-1">
          <Typography className="text-gray-500">Plant Name</Typography>
          <Typography className="text-gray-800" variant="text-md" weight="semibold">
            {job.plant_name}
          </Typography>
        </div>
        {showRepairSystem && (
          <div className="flex-1">
            <Typography className="text-gray-500">Repair System:</Typography>
            <Typography className="text-gray-800" variant="text-md" weight="semibold">
              {repairSystemTitle}
            </Typography>
          </div>
        )}
        {showRepairType && (
          <div className="flex-1">
            <Typography className="text-gray-500">Repair Type:</Typography>
            <Typography className="text-gray-800" variant="text-md" weight="semibold">
              {repairType}
            </Typography>
          </div>
        )}
      </div>
      <div className="my-4 flex justify-between gap-4">
        <div className="flex-1">
          <Typography className="text-gray-500">Design Pressure (psi):</Typography>
          <Typography className="text-gray-800" variant="text-md" weight="semibold">
            {job.design_pressure}
          </Typography>
        </div>
        <div className="flex-1">
          <Typography className="text-gray-500">Design Temperature:</Typography>
          <Typography className="text-gray-800" variant="text-md" weight="semibold">
            {job.design_temperature}
          </Typography>
        </div>
      </div>
      <div className="flex justify-between gap-3">
        <Button data="duplicate-btn" className="flex-1" onClick={() => setJobIdToDuplicate(job.id)}>
          Duplicate
        </Button>
        {canDeleteJob && (
          <Button data="delete-btn" className="flex-1" onClick={() => setJobIdToRemove(job.id)}>
            Delete
          </Button>
        )}
      </div>
    </div>
  );
}
